import { useState, useEffect, useCallback } from "react";
import { X, UserCircle, Mail, Phone, MapPin, Calendar, Activity, Dumbbell, Apple, ClipboardList, ChevronRight, FileDown, Loader2, Ruler } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;
const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });

export function ClientDetailPanel({ client, onClose, trainingPlans, nutritionPlans, anamneseRecords }) {
  const [bioData, setBioData] = useState(null);
  const [bioDiagnoses, setBioDiagnoses] = useState([]);
  const [bioGoals, setBioGoals] = useState([]);
  const [loading, setLoading] = useState(false);

  const clientTraining = trainingPlans.filter(p => p.client_id === client.id);
  const clientNutrition = nutritionPlans.filter(p => p.client_id === client.id);
  const fullName = `${client.vorname} ${client.nachname}`.toLowerCase();
  const clientAnamnese = anamneseRecords.filter(r => (r.client_name || '').toLowerCase().includes(fullName) || fullName.includes((r.client_name || '').toLowerCase()));

  const loadBioData = useCallback(async () => {
    if (!client.bio_client_id) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/bio/clients/${client.bio_client_id}`, { headers: getHeaders() });
      setBioData(res.data.client || res.data);
      setBioDiagnoses(res.data.diagnoses || []);
      setBioGoals(res.data.goals || []);
    } catch { /* no bio data */ }
    setLoading(false);
  }, [client.bio_client_id]);

  useEffect(() => { loadBioData(); }, [loadBioData]);

  const handleExportTrainingPDF = async (planId) => {
    try {
      const res = await axios.get(`${API}/api/training-plans/${planId}/pdf`, { headers: getHeaders(), responseType: "blob", timeout: 60000 });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const link = document.createElement("a"); link.href = url; link.setAttribute("download", `Trainingsplan.pdf`);
      document.body.appendChild(link); link.click(); document.body.removeChild(link); window.URL.revokeObjectURL(url);
    } catch { /* ignore */ }
  };

  const handleExportNutritionPDF = async (planId) => {
    try {
      const res = await axios.get(`${API}/api/nutrition-plans/${planId}/pdf?lang=de`, { headers: getHeaders(), responseType: "blob", timeout: 60000 });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const link = document.createElement("a"); link.href = url; link.setAttribute("download", `Ernaehrungsplan.pdf`);
      document.body.appendChild(link); link.click(); document.body.removeChild(link); window.URL.revokeObjectURL(url);
    } catch { /* ignore */ }
  };

  const latestDiag = bioDiagnoses.length > 0 ? bioDiagnoses[bioDiagnoses.length - 1] : null;

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="client-detail-panel">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative w-full max-w-xl bg-white shadow-2xl overflow-y-auto animate-in slide-in-from-right duration-200">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-slate-100 flex items-center justify-center">
              <UserCircle className="w-6 h-6 text-slate-400" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-900" data-testid="detail-client-name">{client.vorname} {client.nachname}</h2>
              {client.geschlecht && <span className="text-xs text-slate-400">{client.geschlecht === "m" ? "Männlich" : client.geschlecht === "f" ? "Weiblich" : "Divers"}</span>}
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-slate-100" data-testid="close-detail-panel"><X className="w-5 h-5 text-slate-400" /></button>
        </div>

        <div className="p-6 space-y-6">
          {/* Personal Info */}
          <Section title="Persönliche Daten" icon={<UserCircle className="w-4 h-4" />}>
            <div className="grid grid-cols-2 gap-3">
              {client.email && <InfoRow icon={<Mail className="w-3.5 h-3.5" />} label="E-Mail" value={client.email} />}
              {client.telefon && <InfoRow icon={<Phone className="w-3.5 h-3.5" />} label="Telefon" value={client.telefon} />}
              {client.adresse && <InfoRow icon={<MapPin className="w-3.5 h-3.5" />} label="Adresse" value={client.adresse} />}
              {client.geburtsdatum && <InfoRow icon={<Calendar className="w-3.5 h-3.5" />} label="Geburtsdatum" value={client.geburtsdatum} />}
              {client.groesse && <InfoRow icon={<Ruler className="w-3.5 h-3.5" />} label="Größe" value={`${client.groesse} cm`} />}
            </div>
            {client.notizen && <p className="text-xs text-slate-500 mt-3 bg-slate-50 p-2 rounded">{client.notizen}</p>}
          </Section>

          {/* Bioimpedance */}
          <Section title="Bioimpedanz" icon={<Activity className="w-4 h-4 text-emerald-500" />} count={bioDiagnoses.length} color="emerald">
            {loading ? <Loader2 className="w-5 h-5 animate-spin text-slate-300 mx-auto" /> : !client.bio_client_id ? (
              <p className="text-xs text-slate-400">Keine Bioimpedanz-Daten verknüpft</p>
            ) : latestDiag ? (
              <div>
                <p className="text-[10px] text-slate-400 mb-2">Letzte Messung: {latestDiag.datum || "—"}</p>
                <div className="grid grid-cols-3 gap-2">
                  {latestDiag.gewicht && <Stat label="Gewicht" value={`${latestDiag.gewicht} kg`} color="text-blue-600" />}
                  {latestDiag.koerperfett && <Stat label="Körperfett" value={`${latestDiag.koerperfett}%`} color="text-rose-600" />}
                  {latestDiag.muskelmasse && <Stat label="Muskelmasse" value={`${latestDiag.muskelmasse} kg`} color="text-emerald-600" />}
                  {latestDiag.viszerales_fett && <Stat label="Viszerales Fett" value={latestDiag.viszerales_fett} color="text-amber-600" />}
                  {latestDiag.bmi && <Stat label="BMI" value={latestDiag.bmi} color="text-violet-600" />}
                  {latestDiag.wasser && <Stat label="Wasser" value={`${latestDiag.wasser}%`} color="text-cyan-600" />}
                </div>
                {bioDiagnoses.length > 1 && <p className="text-[10px] text-slate-400 mt-2">{bioDiagnoses.length} Messungen insgesamt</p>}
                {bioGoals.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-slate-100">
                    <p className="text-[10px] font-semibold text-slate-500 uppercase mb-1">Ziele</p>
                    <div className="flex flex-wrap gap-1.5">
                      {bioGoals.map((g, i) => <Badge key={i} variant="outline" className="text-[10px]">{g.ziel}: {g.wert} {g.einheit}</Badge>)}
                    </div>
                  </div>
                )}
              </div>
            ) : <p className="text-xs text-slate-400">Keine Messungen vorhanden</p>}
          </Section>

          {/* Training Plans */}
          <Section title="Trainingspläne" icon={<Dumbbell className="w-4 h-4 text-amber-500" />} count={clientTraining.length} color="amber">
            {clientTraining.length === 0 ? <p className="text-xs text-slate-400">Keine Trainingspläne zugewiesen</p> : (
              <div className="space-y-2">
                {clientTraining.map(p => {
                  const dayCount = p.days?.length || 0;
                  const exCount = p.days?.reduce((s, d) => s + (d.exercises?.length || 0), 0) || 0;
                  return (
                    <div key={p.id} className="flex items-center justify-between p-3 bg-amber-50 border border-amber-100 rounded-lg" data-testid={`detail-training-${p.id}`}>
                      <div>
                        <p className="text-sm font-semibold text-slate-900">{p.name}</p>
                        <p className="text-[10px] text-slate-500">{dayCount} Tage • {exCount} Übungen • {p.category || "—"}</p>
                      </div>
                      <div className="flex gap-1">
                        <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => handleExportTrainingPDF(p.id)}><FileDown className="w-3.5 h-3.5 text-slate-400" /></Button>
                        <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => window.location.href = '/training-plans'}><ChevronRight className="w-3.5 h-3.5 text-slate-400" /></Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </Section>

          {/* Nutrition Plans */}
          <Section title="Ernährungspläne" icon={<Apple className="w-4 h-4 text-emerald-500" />} count={clientNutrition.length} color="emerald">
            {clientNutrition.length === 0 ? <p className="text-xs text-slate-400">Keine Ernährungspläne zugewiesen</p> : (
              <div className="space-y-2">
                {clientNutrition.map(p => (
                  <div key={p.id} className="flex items-center justify-between p-3 bg-emerald-50 border border-emerald-100 rounded-lg" data-testid={`detail-nutrition-${p.id}`}>
                    <div>
                      <p className="text-sm font-semibold text-slate-900">{p.name}</p>
                    </div>
                    <div className="flex gap-1">
                      <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => handleExportNutritionPDF(p.id)}><FileDown className="w-3.5 h-3.5 text-slate-400" /></Button>
                      <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => window.location.href = '/nutrition'}><ChevronRight className="w-3.5 h-3.5 text-slate-400" /></Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Section>

          {/* Anamnese Records */}
          <Section title="Anamnese" icon={<ClipboardList className="w-4 h-4 text-violet-500" />} count={clientAnamnese.length} color="violet">
            {clientAnamnese.length === 0 ? <p className="text-xs text-slate-400">Keine Anamnese-Einträge</p> : (
              <div className="space-y-2">
                {clientAnamnese.map((r, i) => (
                  <div key={r.id || i} className="p-3 bg-violet-50 border border-violet-100 rounded-lg" data-testid={`detail-anamnese-${i}`}>
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-semibold text-slate-900">{r.client_name || "Anamnese"}</p>
                      <span className="text-[10px] text-slate-400">{r.created_at ? new Date(r.created_at).toLocaleDateString("de-DE") : ""}</span>
                    </div>
                    {r.raw_analysis && <p className="text-[10px] text-slate-500 mt-1 line-clamp-3">{typeof r.raw_analysis === 'string' ? r.raw_analysis.slice(0, 200) + '...' : ''}</p>}
                  </div>
                ))}
              </div>
            )}
          </Section>
        </div>
      </div>
    </div>
  );
}

function Section({ title, icon, children, count, color = "slate" }) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        {icon}
        <h3 className="text-sm font-bold text-slate-800">{title}</h3>
        {count > 0 && <span className={`text-[10px] px-1.5 py-0.5 rounded-full bg-${color}-100 text-${color}-700 font-semibold`}>{count}</span>}
      </div>
      {children}
    </div>
  );
}

function InfoRow({ icon, label, value }) {
  return (
    <div className="flex items-start gap-2 text-xs">
      <span className="text-slate-400 mt-0.5">{icon}</span>
      <div>
        <p className="text-[10px] text-slate-400 font-medium">{label}</p>
        <p className="text-slate-700">{value}</p>
      </div>
    </div>
  );
}

function Stat({ label, value, color }) {
  return (
    <div className="bg-white border border-slate-100 rounded-lg p-2 text-center">
      <p className={`text-sm font-bold ${color}`}>{value}</p>
      <p className="text-[9px] text-slate-400 uppercase">{label}</p>
    </div>
  );
}
